import React, { useState, useEffect, useRef } from 'react';
import { Modal, Spin, Tabs, Form, Input, Button, message } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';

const WxLoginModal = ({ visible, onCancel }) => {
  const { login } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('wx');
  const [form] = Form.useForm();
  const [sceneStr, setSceneStr] = useState('');
  const [error, setError] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    if (visible) {
      setError(''); // 清除之前的错误
      initScene();

      // 检查URL参数中是否有登录信息
      checkUrlLoginParams();
    }
  }, [visible]);

  // 如果发生错误，显示错误消息
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const errorMsg = params.get('error');
    if (errorMsg) {
      message.error(decodeURIComponent(errorMsg));
    }
  }, []);

  useEffect(() => {
    // 添加消息监听
    const handleMessage = async (event) => {
      // 验证来源
      const allowedOrigins = [
        'https://www.certexam.cn',
        'http://localhost:3399',
        'https://api.certexam.cn',
        window.location.origin
      ];

      console.log('Received message:', event.data, 'from origin:', event.origin); // 添加更详细的日志

      // 使用更宽松的来源验证
      const isAllowedOrigin = allowedOrigins.some(origin =>
        event.origin === origin || event.origin.includes('certexam.cn')
      );

      if (!isAllowedOrigin) {
        console.log('Invalid origin:', event.origin, 'allowed origins:', allowedOrigins);
        return;
      }

      // 处理多种可能的消息格式
      if (event.data.type === 'wxLoginSuccess' ||
          (event.data.token && event.data.user) ||
          (event.data.data && event.data.data.token)) {
        try {
          let token, user;

          if (event.data.type === 'wxLoginSuccess') {
            token = event.data.data.token;
            user = event.data.data.user;
          } else if (event.data.token) {
            token = event.data.token;
            user = event.data.user;
          } else if (event.data.data && event.data.data.token) {
            token = event.data.data.token;
            user = event.data.data.user;
          }

          console.log('Login data extracted:', { token, user }); // 添加日志

          if (!token || !user) {
            throw new Error('Invalid login data received');
          }

          const result = await login(token, user);
          if (result.success) {
            message.success('登录成功');
            onCancel();
          } else {
            throw new Error(result.message || '登录失败');
          }
        } catch (error) {
          console.error('Login error:', error);
          message.error('登录失败，请重试');
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [login, onCancel]);

  // 加载微信登录脚本
  const loadWxLoginScript = () => {
    return new Promise((resolve, reject) => {
      if (window.WxLogin) {
        resolve(window.WxLogin);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://res.wx.qq.com/connect/zh_CN/htmledition/js/wxLogin.js';
      script.async = true;
      script.onload = () => resolve(window.WxLogin);
      script.onerror = () => {
        message.error('微信登录加载失败，请刷新重试');
        reject(new Error('Failed to load WxLogin script'));
      };
      document.body.appendChild(script);
    });
  };

  // 检查URL中是否包含登录信息
  const checkUrlLoginParams = () => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const loginSuccess = params.get('loginSuccess');

    if (token && loginSuccess === 'true') {
      debugger;
      console.log('Found login info in URL params');
      // 清除URL参数
      const newUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, newUrl);

      // 尝试登录
      handleTokenLogin(token);
    }
  };

  // 处理token登录
  const handleTokenLogin = async (token) => {
    try {
      console.log('Attempting to login with token from URL');
      // 从token中解析用户信息
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));

      const payload = JSON.parse(jsonPayload);
      console.log('Token payload:', payload);

      // 使用token登录
      const result = await login(token, {
        id: payload.userId,
        type: 'wechat'
      });

      if (result.success) {
        message.success('登录成功');
        onCancel();
      } else {
        throw new Error(result.message || '登录失败');
      }
    } catch (error) {
      console.error('Token login error:', error);
      message.error('登录失败，请重试');
    }
  };

  // 初始化微信登录二维码
  const initWxLogin = async (sceneStr) => {
    try {
      setError('');
      if (!containerRef.current) {
        console.error('Container ref is null');
        return;
      }

      // 清空容器
      containerRef.current.innerHTML = '';

      // 创建内部容器用于微信登录组件
      const innerContainer = document.createElement('div');
      innerContainer.id = 'wx-login-container';

      // 直接将内部容器添加到DOM中
      containerRef.current.appendChild(innerContainer);

      // 加载微信登录脚本
      const WxLogin = await loadWxLoginScript();

      // 获取当前域名作为回调URL
      // 关键修复：确保生产环境使用www前缀
      let baseCallbackUrl;
      if (window.location.hostname === 'certexam.cn') {
        // 如果当前域名是certexam.cn（没有www），则强制使用www
        baseCallbackUrl = 'https://www.certexam.cn/api/auth/wx/callback';
      } else if (window.location.hostname === 'www.certexam.cn') {
        // 如果已经有www，则直接使用当前域名
        baseCallbackUrl = `${window.location.origin}/api/auth/wx/callback`;
      } else {
        // 本地开发环境或其他环境
        baseCallbackUrl = `${window.location.origin}/api/auth/wx/callback`;
      }

      console.log('Using callback URL:', baseCallbackUrl);

      // 检测浏览器类型
      const isChrome = /Chrome/.test(navigator.userAgent) && !/Edge|Edg/.test(navigator.userAgent);
      console.log('Browser detection - Chrome:', isChrome);

      // 创建微信登录对象
      try {
        new WxLogin({
          self_redirect: false,
          id: 'wx-login-container',
          appid: 'wxa6b3212f4e2ef2bb',
          scope: 'snsapi_login',
          redirect_uri: baseCallbackUrl,
          state: sceneStr,
          style: 'black',
          href: 'data:text/css;base64,LmltcG93ZXJCb3ggLnFyY29kZSB7d2lkdGg6IDIwMHB4OyBoZWlnaHQ6IGF1dG87IG1hcmdpbi10b3A6IDEwMHB4OyBib3JkZXI6IDFweCBzb2xpZCAjZTJlMmUyO30NCi5pbXBvd2VyQm94IC50aXRsZSB7ZGlzcGxheTogbm9uZTt9DQouaW1wb3dlckJveCAuaW5mbyB7d2lkdGg6IDIwMHB4O30NCi5zdGF0dXNfaWNvbiB7ZGlzcGxheTogbm9uZX0NCi5pbXBvd2VyQm94IC5zdGF0dXMge2Rpc3BsYXk6IG5vbmU7fQ0KLmltcG93ZXJCb3gge3dpZHRoOiAxMDAlICFpbXBvcnRhbnQ7IGJvcmRlcjogbm9uZSAhaW1wb3J0YW50OyBtYXJnaW46IDAgYXV0byAhaW1wb3J0YW50OyBwYWRkaW5nOiAwICFpbXBvcnRhbnQ7fQ0KLndyYXAge2JhY2tncm91bmQtY29sb3I6IHRyYW5zcGFyZW50ICFpbXBvcnRhbnQ7fQ=='
        });

        console.log('WxLogin initialized successfully');
      } catch (error) {
        console.error('WxLogin initialization error:', error);
        setError('微信登录初始化失败，请刷新重试');
      }

      // 添加本地测试辅助按钮（仅在开发环境）
      if (process.env.NODE_ENV === 'development') {
        const testButton = document.createElement('button');
        testButton.textContent = '模拟扫码成功（仅开发环境）';
        testButton.className = 'mock-login-button';

        testButton.onclick = async () => {
          try {
            setLoading(true);
            const response = await fetch('/api/auth/wx/mock-login', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ sceneStr })
            });

            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.message || '模拟登录失败');
            }

            const data = await response.json();
            console.log('Mock login response:', data);

            if (data.success) {
              const result = await login(data.token, data.user);
              if (result.success) {
                message.success('模拟登录成功');
                onCancel();
              } else {
                throw new Error(result.message || '登录失败');
              }
            } else {
              throw new Error(data.message || '模拟登录失败');
            }
          } catch (error) {
            console.error('Mock login error:', error);
            message.error('模拟登录失败: ' + error.message);
          } finally {
            setLoading(false);
          }
        };

        // containerRef.current.appendChild(testButton);
      }

      // 添加全局回调函数，以防postMessage失败
      window.wxLoginCallback = async (data) => {
        try {
          console.log('Global wxLoginCallback called with data:', data);
          if (data && data.token && data.user) {
            const result = await login(data.token, data.user);
            if (result.success) {
              message.success('登录成功');
              onCancel();
            } else {
              throw new Error(result.message || '登录失败');
            }
          }
        } catch (error) {
          console.error('Login callback error:', error);
          message.error('登录失败，请重试');
        }
      };

    } catch (error) {
      console.error('初始化微信登录失败:', error);
      setError('微信登录初始化失败，请刷新重试');
      message.error('微信登录初始化失败，请刷新重试');
    }
  };

  const initScene = async () => {
    try {
      const response = await fetch('/api/auth/wx/init-scene', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();

      if (data.success) {
        setSceneStr(data.data.sceneStr);
        setLoading(false);

        // 场景初始化成功后，如果当前是微信登录标签，则初始化微信登录二维码
        if (activeTab === 'wx') {
          setTimeout(() => {
            initWxLogin(data.data.sceneStr);
          }, 100);
        }
      } else {
        message.error('初始化失败，请重试');
      }
    } catch (error) {
      console.error('初始化场景失败:', error);
      message.error('初始化失败，请重试');
    }
  };

  // 当切换到微信登录标签时，初始化微信登录
  useEffect(() => {
    if (activeTab === 'wx' && sceneStr && !loading) {
      initWxLogin(sceneStr);
    }
  }, [activeTab, sceneStr, loading]);

  const handleEmailLogin = async (values) => {
    try {
      const result = await login(values.email, values.password);
      if (result.success) {
        message.success('登录成功');
        onCancel();
      } else {
        message.error(result.message || '登录失败');
      }
    } catch (error) {
      message.error('登录失败: ' + (error.message || '未知错误'));
    }
  };

  const items = [
    {
      key: 'wx',
      label: '微信登录',
      children: (
        <div style={{ textAlign: 'center' }}>
          {loading ? (
            <Spin tip="加载中..." />
          ) : (
            <div className="wx-login-container">
              <div className="wx-qrcode-wrapper">
                <div
                  ref={containerRef}
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                ></div>
              </div>
            </div>
          )}
        </div>
      )
    },
    {
      key: 'email',
      label: '邮箱登录',
      children: (
        <Form
          form={form}
          name="email_login"
          onFinish={handleEmailLogin}
          style={{ marginTop: 20 }}
          className="dark-theme-form"
        >
          <Form.Item
            name="email"
            rules={[{ required: true, message: '请输入邮箱' }]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="邮箱"
              className="dark-input"
            />
          </Form.Item>
          <Form.Item
            name="password"
            rules={[{ required: true, message: '请输入密码' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="密码"
              className="dark-input"
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              登录
            </Button>
          </Form.Item>
        </Form>
      )
    }
  ];

  return (
    <Modal
      title="用户登录"
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={400}
      bodyStyle={{ padding: '12px 24px' }} // 减少内边距
      className="wx-login-modal" // 添加类名，便于自定义样式
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={items}
        centered
      />
    </Modal>
  );
};

export default WxLoginModal;